import React, { useState } from 'react';
import { 
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@ellucian/react-design-system/core';

interface SessionTimeoutWarningModalProps {
    open: boolean;
    secondsRemaining: number;
    warningType: 'idle' | 'session';
    onStayLoggedIn: () => void;
    onLogout: () => void;
    onDismiss: () => void;
    onSignIn?: () => void;
}

const SessionTimeoutWarningModal: React.FC<SessionTimeoutWarningModalProps> = ({ 
    open, 
    secondsRemaining,
    warningType,
    onStayLoggedIn, 
    onLogout,
    onDismiss,
    onSignIn,
}) => {
    const isSessionLimit = warningType === 'session';
    const remainingMinutes = Math.max(Math.floor(secondsRemaining / 60), 0);
    const buttonWidth = 140; // Wide enough for both labels
    const baseButtonStyles = {
        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '110%',
        textTransform: 'capitalize',
        borderRadius: 8,
        minHeight: 44,
        width: buttonWidth,
        justifyContent: 'center',
        transition: 'width 0.2s',
    } as const;
    const primaryFilledTextColor = 'var(--Neutral-neutral-100, #FFF)';

    // Loading states for Continue and Logout buttons
    const [continueLoading, setContinueLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    // Handler for Continue (Stay Logged In)
    const handleContinue = async () => {
        setContinueLoading(true);
        try {
            // Example: call stay-logged-in API (adjust endpoint/payload as needed)
            await onStayLoggedIn();
        } finally {
            setContinueLoading(false);
        }
    };

    // Handler for Logout
    const handleLogout = async () => {
        setLogoutLoading(true);
        try {
            // Example: call logout API (adjust endpoint/payload as needed)
            await onLogout();
        } finally {
            setLogoutLoading(false);
        }
    };

    return (
            <Dialog
                open={open}
                onClose={isSessionLimit ? undefined : onDismiss}
            disableBackdropClick
                disableEscapeKeyDown={isSessionLimit}
            showTitleBarClose={false}
            maxWidth={false}
            id="sessionTimeoutWarning_dialog"
            PaperProps={{
                id: 'sessionTimeoutWarning_paper',
                sx: {
                    display: 'flex',
                    width: { xs: 'calc(100% - 32px)', sm: '648px' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '648px' },
                    borderRadius: '4px',
                    flexDirection: 'column',
                    p: { xs: '16px', sm: '24px' },
                    margin: { xs: '16px', sm: 'auto' },
                    minHeight: { xs: 320, sm: 360 },
                },
            }}
            BackdropProps={{
                sx: {
                    background: 'rgba(0, 0, 0, 0.30) !important',
                },
            }}
        >
            <DialogTitle 
                id="sessionTimeoutWarning_title"
                sx={{
                    pb: 0,
                    pt: 0,
                    px: 0,
                    textAlign: 'left',
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: '24px',
                        fontWeight: 500,
                        lineHeight: '110%',
                        m: 0,
                        p: 0,
                    }}
                >
                    {isSessionLimit ? 'Session Ending' : 'Session Timeout Warning'}
                </Typography>
            </DialogTitle>
            <DialogContent 
                id="sessionTimeoutWarning_content"
                sx={{
                    px: 0,
                    pt: 2,
                    pb: 0,
                }}
            >
                    <Typography id="sessionTimeoutWarning_description">
                        {isSessionLimit
                            ? 'You have reached the maximum 2-hour session duration. You will be signed out soon.'
                            : 'Your session is about to expire due to inactivity.'}
                    </Typography>
                    {isSessionLimit ? (
                        <Typography sx={{ mt: 1 }}>
                            Please save your work. You will be automatically signed out at the 2-hour limit.
                        </Typography>
                    ) : (
                        <Typography sx={{ mt: 1 }}>
                            Select "Continue" to stay signed in, or "Log out" to end your session.
                        </Typography>
                    )}
                    {!isSessionLimit && secondsRemaining > 0 && (
                        <Typography sx={{ mt: 1, fontWeight: 600 }}>
                            Time remaining: {remainingMinutes} minute{remainingMinutes === 1 ? '' : 's'}.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions 
                    id="sessionTimeoutWarning_actions"
                    sx={{
                        px: 0,
                        pt: 3,
                        pb: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    {isSessionLimit ? (
                        <>
                            <Button
                                id="sessionTimeoutWarning_dismiss"
                                onClick={onDismiss}
                                variant="contained"
                                color="secondary"
                                disableElevation
                                sx={{
                                    ...baseButtonStyles,
                                    backgroundColor: 'var(--Neutral-neutral-100, #FFF)',
                                    color: 'var(--Neutral-neutral-600, #151618)',
                                    border: '1px solid var(--Neutral-neutral-300, #D1D5DB)',
                                    '&:hover': {
                                        backgroundColor: 'var(--Neutral-neutral-200, #E5E7EB)',
                                        borderColor: 'var(--Neutral-neutral-400, #9CA3AF)',
                                    },
                                }}
                            >
                                Dismiss
                            </Button>
                            <Button
                                id="sessionTimeoutWarning_signIn"
                                onClick={() => onSignIn && onSignIn()}
                                variant="contained"
                                color="primary"
                                disableElevation
                                sx={{
                                    ...baseButtonStyles,
                                    color: primaryFilledTextColor,
                                    backgroundColor: 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                                    width: buttonWidth,
                                    position: 'relative',
                                }}
                            >
                                Sign in
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                id="sessionTimeoutWarning_logOut"
                                onClick={handleLogout}
                                variant="contained"
                                color="primary"
                                disableElevation
                                disabled={logoutLoading}
                                sx={{
                                    ...baseButtonStyles,
                                    color: primaryFilledTextColor,
                                    backgroundColor: logoutLoading ? 'var(--Neutral-neutral-400, #8E8E93)' : 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                                    width: buttonWidth,
                                    position: 'relative',
                                }}
                            >
                                <span style={{ opacity: 0, pointerEvents: 'none', display: 'inline-block' }}>Signing out...</span>
                                <span style={{ position: 'absolute', left: 0, right: 0, width: '100%', textAlign: 'center' }}>{logoutLoading ? 'Signing out...' : 'Sign out'}</span>
                            </Button>
                            <Button
                                id="sessionTimeoutWarning_stayLoggedIn"
                                onClick={handleContinue}
                                variant="contained"
                                color="primary"
                                autoFocus
                                disableElevation
                                disabled={continueLoading}
                                sx={{
                                    ...baseButtonStyles,
                                    color: primaryFilledTextColor,
                                    backgroundColor: continueLoading ? 'var(--Neutral-neutral-400, #8E8E93)' : 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                                    width: buttonWidth,
                                    position: 'relative',
                                }}
                            >
                                <span style={{ opacity: 0, pointerEvents: 'none', display: 'inline-block' }}>Continuing...</span>
                                <span style={{ position: 'absolute', left: 0, right: 0, width: '100%', textAlign: 'center' }}>{continueLoading ? 'Continuing...' : 'Continue'}</span>
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
    );
};

export default SessionTimeoutWarningModal;
